import React from "react";
import logoMombasa from "../../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../../core/img/branch-logos/logo_texastrack.png";

// The customer-facing Quotation print (2026-10-28, replicates the customer's own
// sample): maroon "Your Link With Security" banner + branch logo, a maroon title
// bar, a boxed customer / quotation-number block, the cover letter, a maroon-
// headed line table, a totals box ending in a maroon "Total Incl VAT" row, the
// footer notes as "Additional Charges", Prepared/Approved By + signature and the
// company footer. Quotation-only on purpose - LPO/Stock/vouchers keep the shared
// StockDocumentPreview.jsx unchanged.
//
// Props are the object buildQuotationPrintProps.js builds. Like
// StockDocumentPreview, this renders identically live (QuotationDeliveryModal's
// preview, which html2canvas captures for the emailed PDF) and as static markup
// for the print popup (ReactDOMServer.renderToStaticMarkup) - so inline styles
// only, no app stylesheet, no webfonts, and absolute logo URLs.

const MAROON = "#8B0A1A";
const PINK_BG = "#FDF0F2";
const PINK_BORDER = "#E8B4BC";
const ROW_BORDER = "#D9D9D9";
const FOOTER_GREY = "#EDEDED";
const FONT_FAMILY = '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
// System script faces only - the print popup fires window.print() before a
// webfont could ever load.
const SCRIPT_FONT = '"Brush Script MT", "Segoe Script", "Lucida Handwriting", cursive';

// Pads a short quotation's table to a proper page height, like the sample.
const MIN_TABLE_ROWS = 9;

const LOGO_BY_BRANCH_CODE = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};

// Absolute URL - the print popup is about:blank, where a root-relative webpack
// path would not resolve (same fix as StockDocumentPreview.jsx).
const logoUrlFor = (branchCode) =>
  new URL(LOGO_BY_BRANCH_CODE[branchCode] || logoMombasa, window.location.origin).href;

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtyText = (n) => (Number(n) || 0).toFixed(2);

// Company.Email holds "accounts@x.com, KRA PIN : P05..." in one setting - split
// it so the footer can print "Email: ... | KRA PIN: ..." as two labelled parts.
const splitEmailAndPin = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return { email: "", pin: "" };
  const comma = text.indexOf(",");
  if (comma < 0) return { email: text, pin: "" };
  const email = text.slice(0, comma).trim();
  const rest = text.slice(comma + 1).trim();
  // "KRA PIN : P05..." -> "P05..."; anything without a label is kept as typed.
  const pin = /pin/i.test(rest) && rest.includes(":") ? rest.slice(rest.indexOf(":") + 1).trim() : rest;
  return { email, pin };
};

const Blank = ({ width = 200 }) => (
  <span style={{ display: "inline-block", borderBottom: "1px solid #999", minWidth: width }}>&nbsp;</span>
);

const InfoLine = ({ label, value, blankWidth, last = false }) => (
  <div style={{ marginBottom: last ? 0 : 7 }}>
    <span style={{ color: MAROON, fontWeight: 700 }}>{label} :</span>&nbsp;{" "}
    {value ? <span style={{ fontWeight: 600 }}>{value}</span> : <Blank width={blankWidth} />}
  </div>
);

const th = {
  background: MAROON,
  color: "#fff",
  fontWeight: 700,
  padding: "6px 5px",
  border: `1px solid ${MAROON}`,
  fontSize: 10.5,
};
const td = { borderBottom: `1px solid ${ROW_BORDER}`, borderLeft: `1px solid ${ROW_BORDER}`, padding: "5px 5px", fontSize: 10.5 };
const totalsCell = { padding: "6px 8px", borderBottom: `1px solid ${ROW_BORDER}` };
const avoidBreak = { pageBreakInside: "avoid", breakInside: "avoid" };
// The print popup is written without a doctype (quirks mode), where tables do
// not inherit font size/colour from the page - so every table states its own.
const tableBase = { fontSize: 11, color: "#222", fontFamily: FONT_FAMILY };

const QuotationPrintPreview = ({
  docNo,
  docDate,
  branchLabel,
  letterheadBannerText,
  quotationSubject,
  partyName,
  partyAddress,
  partyMobile,
  salespersonName,
  salespersonPhone,
  vatPercent,
  lines = [],
  totals = { subTotal: 0, vatAmount: 0, netAmount: 0 },
  preparedByName,
  approvedByName,
  approvedByDesignation,
  approvedDate,
  signatureImageUrl,
  coverLetterHtml,
  footerNotesHtml,
  companyName = "TEXAS ALARMS (K) LTD",
  companyAddress,
  companyPhone,
  companyEmail,
}) => {
  const totalQty = lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
  const fillerCount = Math.max(0, MIN_TABLE_ROWS - lines.length);
  const { email, pin } = splitEmailAndPin(companyEmail);
  const addressLine = [companyAddress, companyPhone && `Tel: ${companyPhone}`].filter(Boolean).join(" | ");
  const contactLine = [email && `Email: ${email}`, pin && `KRA PIN: ${pin}`].filter(Boolean).join(" | ");
  const discount = Number(totals.discountAmount) || 0;

  return (
    // print-color-adjust: browsers drop background colours when printing unless
    // told otherwise - without it the maroon bars and header row print white.
    <div
      style={{
        fontFamily: FONT_FAMILY,
        fontSize: 11,
        lineHeight: 1.3,
        color: "#222",
        background: "#fff",
        padding: 18,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {/* 1. Banner + logo */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10, ...tableBase }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle", paddingRight: 14 }}>
              <div style={{ background: MAROON, color: "#fff", padding: "10px 16px", borderRadius: "0 28px 28px 0" }}>
                <span style={{ fontFamily: SCRIPT_FONT, fontStyle: "italic", fontSize: 26, letterSpacing: 0.5 }}>
                  Your Link With Security
                </span>
              </div>
              {letterheadBannerText && (
                <div style={{ marginTop: 6, fontSize: 9, color: "#666", letterSpacing: 0.3 }}>{letterheadBannerText}</div>
              )}
            </td>
            <td style={{ width: 1, whiteSpace: "nowrap", textAlign: "right", verticalAlign: "middle" }}>
              <img src={logoUrlFor(branchLabel)} alt={companyName} style={{ height: 80 }} />
            </td>
          </tr>
        </tbody>
      </table>

      {/* 2. Title bar */}
      <div
        style={{
          background: MAROON,
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: 0.6,
          textAlign: "center",
          padding: "7px 10px",
          marginBottom: 10,
        }}
      >
        {quotationSubject || "QUOTATION"}
      </div>

      {/* 3. Customer / quotation number box */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${PINK_BORDER}`, marginBottom: 12, ...tableBase }}>
        <tbody>
          <tr>
            <td style={{ width: "55%", verticalAlign: "top", padding: "10px 12px", borderRight: `1px solid ${PINK_BORDER}` }}>
              <InfoLine label="Customer" value={partyName} blankWidth={220} />
              <InfoLine label="Address" value={partyAddress} blankWidth={230} />
              <InfoLine label="Mobile No." value={partyMobile} blankWidth={200} last />
            </td>
            <td style={{ verticalAlign: "top", padding: "10px 12px", background: PINK_BG }}>
              <InfoLine label="Quotation No." value={docNo} blankWidth={140} />
              <InfoLine label="Date" value={docDate} blankWidth={140} last />
            </td>
          </tr>
        </tbody>
      </table>

      {/* 4. Greeting + cover letter */}
      {coverLetterHtml && (
        <div style={{ marginBottom: 12, fontSize: 10.5, ...avoidBreak }}>
          <div style={{ color: MAROON, fontWeight: 700, marginBottom: 5 }}>
            Dear {(partyName || "Sir/Madam").toUpperCase()},
          </div>
          <div dangerouslySetInnerHTML={{ __html: coverLetterHtml }} />
        </div>
      )}

      {/* 5. Line items */}
      <table style={{ width: "100%", borderCollapse: "collapse", borderRight: `1px solid ${ROW_BORDER}`, marginBottom: 12, ...tableBase }}>
        <thead style={{ display: "table-header-group" }}>
          <tr>
            <th style={{ ...th, width: 40, textAlign: "center" }}>Sl No</th>
            {/* Qty after the product (2026-10-30, by instruction). */}
            <th style={{ ...th, textAlign: "left" }}>Product Name / Description</th>
            <th style={{ ...th, width: 58, textAlign: "center" }}>Qty</th>
            <th style={{ ...th, width: 92, textAlign: "right" }}>Unit Price (KSh)</th>
            <th style={{ ...th, width: 82, textAlign: "right" }}>VAT (KSh)</th>
            <th style={{ ...th, width: 98, textAlign: "right" }}>Line Total (KSh)</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, idx) => (
            <tr key={idx} style={avoidBreak}>
              <td style={{ ...td, textAlign: "center" }}>{idx + 1}</td>
              <td style={td}>{line.productName}</td>
              <td style={{ ...td, textAlign: "center" }}>{qtyText(line.qty)}</td>
              <td style={{ ...td, textAlign: "right" }}>{line.price == null ? "-" : money(line.price)}</td>
              <td style={{ ...td, textAlign: "right" }}>{money(line.vatAmount)}</td>
              <td style={{ ...td, textAlign: "right" }}>{money(line.amount)}</td>
            </tr>
          ))}
          {Array.from({ length: fillerCount }).map((_, idx) => (
            <tr key={`filler-${idx}`} style={avoidBreak}>
              {Array.from({ length: 6 }).map((__, c) => (
                <td key={c} style={td}>&nbsp;</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ ...avoidBreak, background: FOOTER_GREY, fontWeight: 700 }}>
            <td style={td} />
            <td style={{ ...td, textAlign: "center" }}>Grand Total in KSh</td>
            <td style={{ ...td, textAlign: "center" }}>{qtyText(totalQty)}</td>
            <td style={{ ...td, textAlign: "right" }} colSpan={3}>{money(totals.netAmount)}</td>
          </tr>
        </tfoot>
      </table>

      {/* 6 + 7. Additional charges (footer notes) beside the totals box */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14, ...avoidBreak, ...tableBase }}>
        <tbody>
          <tr>
            <td style={{ width: "56%", verticalAlign: "top", paddingRight: 16 }}>
              {footerNotesHtml && (
                <div style={{ border: `1px solid ${PINK_BORDER}`, background: "#fff" }}>
                  <div style={{ background: PINK_BG, color: MAROON, fontWeight: 700, padding: "5px 8px", borderBottom: `1px solid ${PINK_BORDER}` }}>
                    Additional Charges (if applicable)
                  </div>
                  {/* Mirrors the Quill editor the notes are typed in (no
                      paragraph margins, 1.5em list indent) - an inline <style>
                      because the print popup has no app stylesheet. */}
                  <style>{`.quo-footer-notes p{margin:0}.quo-footer-notes ol,.quo-footer-notes ul{margin:0;padding-left:1.5em}.quo-footer-notes li{margin:0}`}</style>
                  <div
                    className="quo-footer-notes"
                    style={{ padding: "6px 8px", fontSize: 10.5, lineHeight: 1.42 }}
                    dangerouslySetInnerHTML={{ __html: footerNotesHtml }}
                  />
                </div>
              )}
            </td>
            <td style={{ verticalAlign: "top" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${ROW_BORDER}`, fontSize: 11 }}>
                <tbody>
                  <tr>
                    <td style={totalsCell}>Sub Total</td>
                    <td style={{ ...totalsCell, textAlign: "right" }}>KSh {money(totals.subTotal)}</td>
                  </tr>
                  {/* Quotation discount (Batch163) - only when there is one. */}
                  {discount > 0 && (
                    <>
                      <tr>
                        <td style={totalsCell}>{totals.discountLabel || "Discount"}</td>
                        <td style={{ ...totalsCell, textAlign: "right" }}>- KSh {money(discount)}</td>
                      </tr>
                      <tr>
                        <td style={totalsCell}>Net</td>
                        <td style={{ ...totalsCell, textAlign: "right" }}>KSh {money((Number(totals.subTotal) || 0) - discount)}</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td style={totalsCell}>{vatPercent == null ? "VAT" : `VAT ${vatPercent}%`}</td>
                    <td style={{ ...totalsCell, textAlign: "right" }}>KSh {money(totals.vatAmount)}</td>
                  </tr>
                  <tr style={{ background: MAROON, color: "#fff", fontWeight: 700 }}>
                    <td style={{ padding: "7px 8px" }}>Total Incl VAT</td>
                    <td style={{ padding: "7px 8px", textAlign: "right" }}>KSh {money(totals.netAmount)}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* 8. Prepared / approved + signature */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, ...avoidBreak, ...tableBase }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "bottom", lineHeight: 1.8 }}>
              <div>
                <span style={{ color: MAROON, fontWeight: 700 }}>Prepared By :</span>&nbsp;{" "}
                {preparedByName ? <strong>{preparedByName}</strong> : <Blank width={180} />}
              </div>
              <div>
                <span style={{ color: MAROON, fontWeight: 700 }}>Approved By :</span>&nbsp;{" "}
                {approvedByName ? (
                  <>
                    <strong>{approvedByName}</strong>
                    {approvedByDesignation && <span> ({approvedByDesignation})</span>}
                  </>
                ) : (
                  <Blank width={180} />
                )}
              </div>
              <div>
                <span style={{ color: MAROON, fontWeight: 700 }}>Date :</span>&nbsp;{" "}
                {approvedDate || <Blank width={120} />}
              </div>
              {/* Batch221 - the quotation's salesperson, only when one is set. */}
              {salespersonName && (
                <div style={{ marginTop: 6, padding: "5px 8px", background: PINK_BG, border: `1px solid ${PINK_BORDER}`, lineHeight: 1.5 }}>
                  <span style={{ color: MAROON, fontWeight: 700 }}>For further info kindly contact :</span>&nbsp;{" "}
                  <strong>{salespersonName}</strong>
                  {salespersonPhone && <span> - {salespersonPhone}</span>}
                </div>
              )}
            </td>
            <td style={{ width: 270, textAlign: "center", verticalAlign: "bottom" }}>
              {signatureImageUrl && (
                // Fixed landscape box + object-fit:cover, as StockDocumentPreview -
                // uploaded seals vary wildly in size/orientation.
                <div style={{ width: 240, height: 100, margin: "0 auto 4px", overflow: "hidden" }}>
                  <img
                    src={signatureImageUrl}
                    alt="Approval signature"
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
                  />
                </div>
              )}
              <div style={{ borderTop: "1px solid #444", paddingTop: 4 }}>
                For and on behalf of
                <br />
                <strong>{companyName}</strong>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* 9. Company footer */}
      <div style={{ borderTop: `2px solid ${MAROON}`, paddingTop: 6, textAlign: "center", fontSize: 10, ...avoidBreak }}>
        <div style={{ color: MAROON, fontWeight: 700, fontSize: 11.5 }}>{companyName}</div>
        {addressLine && <div>{addressLine}</div>}
        {contactLine && <div>{contactLine}</div>}
      </div>
    </div>
  );
};

export default QuotationPrintPreview;
